import axios from 'axios';
import {
  Asset,
  AssetsResponse,
  KnowledgeModule,
  KnowledgeModuleCreate,
  KnowledgeModulesResponse,
  WhatsAppTemplate,
  WhatsAppTemplateCreate,
  WhatsAppTemplatesResponse,
} from '../types';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8100';
const API_KEY = process.env.REACT_APP_API_KEY || '';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Authorization': `Bearer ${API_KEY}`,
  },
});

// Creative Library (meta-ads assets + performance + votes)
export interface LibraryAd {
  name: string;
  campaign: string | null;
  objective: string | null;
  status: string | null;
  spend: number;
}

export interface LibraryAsset {
  id: string;
  name: string;
  type: 'video' | 'image';
  tags: string[];
  url: string;
  account_id: string | null;
  first_used: string | null;
  spend: number;
  impressions: number;
  conversations: number;
  ads_count: number;
  copy: { title: string | null; body: string | null } | null;
  ads: LibraryAd[];
  vote_count: number;
  voted_by_me: boolean;
  archived: boolean;
}

export const getCreativeLibrary = async (voter: string): Promise<{ total: number; assets: LibraryAsset[] }> => {
  const response = await api.get(`/api/v1/meta-ads/asset-stats?voter=${encodeURIComponent(voter)}`);
  return response.data;
};

export const voteAsset = async (assetId: string, voter: string): Promise<{ vote_count: number; voted_by_me: boolean }> => {
  const response = await api.put(`/api/v1/assets/${assetId}/vote?voter=${encodeURIComponent(voter)}`);
  return response.data;
};

export const unvoteAsset = async (assetId: string, voter: string): Promise<{ vote_count: number; voted_by_me: boolean }> => {
  const response = await api.delete(`/api/v1/assets/${assetId}/vote?voter=${encodeURIComponent(voter)}`);
  return response.data;
};

export const archiveAsset = async (assetId: string): Promise<{ archived: boolean }> => {
  const response = await api.put(`/api/v1/assets/${assetId}/archive`);
  return response.data;
};

export const unarchiveAsset = async (assetId: string): Promise<{ archived: boolean }> => {
  const response = await api.delete(`/api/v1/assets/${assetId}/archive`);
  return response.data;
};

// Project Album (album_frames + per-frame funnel from core)
export interface AlbumFrameStats {
  views: number;
  unique_leads: number;
  likes: number;
  shares: number;
  brochure_opens: number;
  timing_n: number;                 // frame.timing samples behind the two below
  load_ms_p50: number | null;       // median time-to-play; null = never measured / always bounced pre-play
  dwell_ms_p50: number | null;      // median time on frame (median, not mean — robust to parked-tab outliers)
}

export interface AlbumFrameRow {
  code: string;
  type: 'reel' | 'landscape' | 'image' | 'review' | 'brochure' | 'cta';
  position: number | null;
  tag: string | null;
  caption: string | null;
  cta: boolean;
  review: { quote: string; name: string; context: string } | null;
  video_url: string | null;
  poster_url: string | null;
  title: string | null;
  description: string | null;
  best_for: string | null;
  audio: string | null;
  duration_s: number | null;
  size_mb: number | null;
  source: string | null;
  transcript_url: string | null;
  tags: string[];
  stats: AlbumFrameStats;
}

export const getAlbumFrames = async (): Promise<{ total: number; frames: AlbumFrameRow[] }> => {
  const response = await api.get('/api/v1/album/frames?include_stats=true');
  return response.data;
};

export const updateAlbumFrameTags = async (code: string, tags: string[]): Promise<AlbumFrameRow> => {
  const response = await api.patch(`/api/v1/album/frames/${code}`, { tags });
  return response.data;
};

// Replace the entire feed order: listed codes get positions 1..n; every other
// frame goes catalog-only. Mirrors marketing-service PUT /album/frames/order.
export const setAlbumFeedOrder = async (codes: string[]): Promise<{ feed_length: number }> => {
  const response = await api.put('/api/v1/album/frames/order', { codes });
  return response.data;
};

export const getAlbumTranscript = async (code: string): Promise<{ text: string; no_audio?: boolean }> => {
  const response = await api.get(`/api/v1/album/frames/${code}/transcript`);
  return response.data;
};

// Assets
export const getAssetFolders = async (): Promise<{ folders: string[] }> => {
  const response = await api.get('/api/v1/asset-folders');
  return response.data;
};

export const getAssets = async (tags?: string, type?: string, folder?: string | null): Promise<AssetsResponse> => {
  const params = new URLSearchParams();
  if (tags) params.append('tags', tags);
  if (type) params.append('type', type);
  if (folder) params.append('folder', folder);
  const response = await api.get(`/api/v1/assets?${params.toString()}`);
  return response.data;
};

export const getAsset = async (id: string): Promise<Asset> => {
  const response = await api.get(`/api/v1/assets/${id}`);
  return response.data;
};

export const createAsset = async (formData: FormData): Promise<Asset> => {
  const response = await api.post('/api/v1/assets', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const updateAsset = async (id: string, formData: FormData): Promise<Asset> => {
  const response = await api.put(`/api/v1/assets/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const deleteAsset = async (id: string): Promise<void> => {
  await api.delete(`/api/v1/assets/${id}`);
};

export const moveAsset = async (id: string, folder: string | null): Promise<Asset> => {
  const response = await api.patch(`/api/v1/assets/${id}/move`, null, {
    params: { folder: folder || '' },
  });
  return response.data;
};

// WhatsApp Templates
export const getWhatsAppTemplates = async (params?: {
  template_type?: string;
  template_scope?: string;
  category?: string;
  status?: string;
  tags?: string;
  parent_id?: string;
  include_retired?: boolean;
}): Promise<WhatsAppTemplatesResponse> => {
  const queryParams = new URLSearchParams();
  if (params?.template_type) queryParams.append('template_type', params.template_type);
  if (params?.template_scope) queryParams.append('template_scope', params.template_scope);
  if (params?.category) queryParams.append('category', params.category);
  if (params?.status) queryParams.append('status', params.status);
  if (params?.tags) queryParams.append('tags', params.tags);
  if (params?.parent_id) queryParams.append('parent_id', params.parent_id);
  if (params?.include_retired) queryParams.append('include_retired', 'true');
  const response = await api.get(`/api/v1/whatsapp-templates?${queryParams.toString()}`);
  return response.data;
};

export const getWhatsAppTemplate = async (id: string): Promise<WhatsAppTemplate> => {
  const response = await api.get(`/api/v1/whatsapp-templates/${id}`);
  return response.data;
};

export const createWhatsAppTemplate = async (template: WhatsAppTemplateCreate): Promise<WhatsAppTemplate> => {
  const response = await api.post('/api/v1/whatsapp-templates', template);
  return response.data;
};

export const updateWhatsAppTemplate = async (id: string, template: WhatsAppTemplateCreate): Promise<WhatsAppTemplate> => {
  const response = await api.put(`/api/v1/whatsapp-templates/${id}`, template);
  return response.data;
};

export const patchWhatsAppTemplate = async (id: string, updates: Partial<WhatsAppTemplateCreate>): Promise<WhatsAppTemplate> => {
  const response = await api.patch(`/api/v1/whatsapp-templates/${id}`, updates);
  return response.data;
};

export const deleteWhatsAppTemplate = async (id: string): Promise<void> => {
  await api.delete(`/api/v1/whatsapp-templates/${id}`);
};

export interface SubmitTemplateResponse {
  success: boolean;
  message: string;
  status: string;
  instructions?: string;
}

export const submitWhatsAppTemplate = async (id: string): Promise<SubmitTemplateResponse> => {
  const response = await api.post(`/api/v1/whatsapp-templates/${id}/submit`);
  return response.data;
};

// Knowledge Modules
export const getKnowledgeModules = async (): Promise<KnowledgeModulesResponse> => {
  const response = await api.get('/api/v1/knowledge-modules');
  return response.data;
};

export const createKnowledgeModule = async (module: KnowledgeModuleCreate): Promise<KnowledgeModule> => {
  const response = await api.post('/api/v1/knowledge-modules', module);
  return response.data;
};

export const updateKnowledgeModule = async (id: string, module: KnowledgeModuleCreate): Promise<KnowledgeModule> => {
  const response = await api.put(`/api/v1/knowledge-modules/${id}`, module);
  return response.data;
};

export const deleteKnowledgeModule = async (id: string): Promise<void> => {
  await api.delete(`/api/v1/knowledge-modules/${id}`);
};
